import { Link } from '@dev-spendesk/grapes';
import { type PayableRuleId } from '@spendesk/bookkeeping-core-types';
import { Trans } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useNotifications } from 'src/core/modules/app/notifications';

import { useBulkUpdatePayableRulesStateMutation } from '../../../hooks/useBulkUpdatePayableRulesStateMutation';
import { getPayableRulesQueryKey } from '../../../hooks/useGetPayableRules';
import { getPayableRulesMCRLimitQueryKey } from '../../../hooks/useGetPayableRulesMCRLimit';

export function useUpdatePayableRulesStateFromTable(companyId: string) {
  const { t } = useTranslation('global');
  const { dangerNotif, successNotif } = useNotifications();
  const queryClient = useQueryClient();
  const { mutateAsync: updatePayableRulesState } =
    useBulkUpdatePayableRulesStateMutation();
  const history = useHistory();

  return async (ruleIds: PayableRuleId[], verb: 'publish' | 'unpublish') => {
    try {
      const result = await updatePayableRulesState({
        payableRuleIds: ruleIds,
        verb,
      });
      if (result.outcome === 'success') {
        const notifKey =
          verb === 'unpublish'
            ? 'bookkeep.integrations.settings.payableRules.rulesUnpublishedWithCount'
            : 'bookkeep.integrations.settings.payableRules.rulesPublishedWithCount';
        queryClient.invalidateQueries(getPayableRulesQueryKey);
        queryClient.invalidateQueries(getPayableRulesMCRLimitQueryKey);

        successNotif(t(notifKey, { count: ruleIds.length }));
      } else if (
        result.outcome === 'failure' &&
        result.reason === 'invalidStateTransition'
      ) {
        history.push(
          `${routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
            company: companyId,
            payableRuleId: result.ruleId,
          })}?edit=true`,
        );
      } else if (
        result.outcome === 'failure' &&
        result.reason === 'mcrLimitReached'
      ) {
        dangerNotif(
          <Trans
            i18nKey="bookkeep.integrations.settings.payableRules.mcrLimits.fullyReachedNotification"
            components={[
              <Link
                key="intercom-message"
                onClick={(event) => {
                  event.preventDefault();
                  window.Intercom('show');
                }}
                href=""
              >
                -
              </Link>,
            ]}
          />,
        );
      }
    } catch {
      dangerNotif(t('misc.somethingWrong'));
    }
  };
}
